import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import {
  createInverter,
  deleteInverter,
  getAllInverters,
  getInverter,
  GetInverterResponse,
  InverterListItem,
  InverterRequest,
  InverterToDeviceWithInverterWithErrors,
  updateInverter,
} from '@app/api/inverter.api'

import type { Status } from '@/types/general-types'
import { getErrorMessage } from '@/utils/utils'

type InverterState = {
  invertersData: Record<string, InverterListItem>
  invertersListStatus: Status
  inverterDetailsData: Record<string, GetInverterResponse>
  inverterDetailsStatusById: Record<string, Status>
  createInverterStatus: Status
  updateInverterStatus: Status
  deleteInverterStatus: Status
  dataAll: InverterToDeviceWithInverterWithErrors[]
  isLoadingInvertersError: boolean
}

const initialState: InverterState = {
  invertersData: {},
  invertersListStatus: 'idle',
  inverterDetailsData: {},
  inverterDetailsStatusById: {},
  createInverterStatus: 'idle',
  updateInverterStatus: 'idle',
  deleteInverterStatus: 'idle',
  dataAll: [],
  isLoadingInvertersError: false,
}

export type InverterUpdateRequest = {
  id: string
  data: InverterRequest
}

export const getAllInvertersData = createAsyncThunk<InverterListItem[]>(
  'inverter/getAllInverter',
  async (_, { rejectWithValue }) => {
    try {
      return await getAllInverters()
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const createInverterData = createAsyncThunk<GetInverterResponse, InverterRequest>(
  'inverter/createInverter',
  async (payload, { rejectWithValue }) => {
    try {
      return await createInverter(payload)
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const updateInverterData = createAsyncThunk<GetInverterResponse, InverterUpdateRequest>(
  'inverter/updateInverter',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      return await updateInverter(id, data)
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const deleteInverterData = createAsyncThunk<GetInverterResponse, string>(
  'inverter/deleteInverter',
  async (id, { rejectWithValue }) => {
    try {
      return await deleteInverter(id)
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const getInverterById = createAsyncThunk<GetInverterResponse, string>(
  'inverter/getInverterById',
  async (id, { rejectWithValue }) => {
    try {
      return await getInverter(id)
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const inverterSlice = createSlice({
  name: 'inverter',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getAllInvertersData.pending, (state) => {
        state.invertersListStatus = 'loading'
      })
      .addCase(getAllInvertersData.rejected, (state) => {
        state.invertersListStatus = 'failed'
      })
      .addCase(getAllInvertersData.fulfilled, (state, action) => {
        state.invertersData = Object.fromEntries(action.payload.map((inverter) => [inverter.id, inverter]))
        state.invertersListStatus = 'succeeded'
      })
      .addCase(getInverterById.pending, (state, action) => {
        state.inverterDetailsStatusById[action.meta.arg] = 'loading'
      })
      .addCase(getInverterById.rejected, (state, action) => {
        state.inverterDetailsStatusById[action.meta.arg] = 'failed'
      })
      .addCase(getInverterById.fulfilled, (state, action) => {
        state.inverterDetailsData[action.payload.id] = action.payload
        state.inverterDetailsStatusById[action.payload.id] = 'succeeded'
      })
      .addCase(createInverterData.pending, (state) => {
        state.createInverterStatus = 'loading'
      })
      .addCase(createInverterData.rejected, (state) => {
        state.createInverterStatus = 'failed'
      })
      .addCase(createInverterData.fulfilled, (state, action) => {
        state.invertersData[action.payload.id] = action.payload
        state.inverterDetailsData[action.payload.id] = action.payload
        state.inverterDetailsStatusById[action.payload.id] = 'succeeded'
        state.createInverterStatus = 'succeeded'
      })
      .addCase(updateInverterData.pending, (state) => {
        state.updateInverterStatus = 'loading'
      })
      .addCase(updateInverterData.rejected, (state) => {
        state.updateInverterStatus = 'failed'
      })
      .addCase(updateInverterData.fulfilled, (state, action) => {
        state.invertersData[action.payload.id] = action.payload
        state.inverterDetailsData[action.payload.id] = action.payload
        state.inverterDetailsStatusById[action.payload.id] = 'succeeded'
        state.updateInverterStatus = 'succeeded'
      })
      .addCase(deleteInverterData.pending, (state) => {
        state.deleteInverterStatus = 'loading'
      })
      .addCase(deleteInverterData.rejected, (state) => {
        state.deleteInverterStatus = 'failed'
      })
      .addCase(deleteInverterData.fulfilled, (state, action) => {
        const id = action.payload.id

        delete state.invertersData[id]
        delete state.inverterDetailsData[id]
        delete state.inverterDetailsStatusById[id]
        state.deleteInverterStatus = 'succeeded'
      })
  },
})

export default inverterSlice.reducer
